import { useSyncExternalStore } from 'react';
import { API_BASE } from '@/shared/api/client';

/**
 * Shared tickers + a server-synchronised clock (§4.3: the dashboard clock shows server time, not the PC clock).
 * The offset is estimated once from the HTTP `Date` header (1 s resolution, mid-point of the round trip).
 */

let offsetMs = 0;
let offsetRequested = false;

interface Ticker {
  interval: number;
  value: number;
  listeners: Set<() => void>;
  timer: ReturnType<typeof setTimeout> | null;
}
const tickers = new Map<number, Ticker>();

export const serverNow = () => Date.now() + offsetMs;
const floorTo = (interval: number) => Math.floor(serverNow() / interval) * interval;

function syncServerOffset() {
  if (offsetRequested || typeof window === 'undefined') return;
  offsetRequested = true;
  const t0 = Date.now();
  fetch(`${API_BASE}/auth/me`, { method: 'HEAD', credentials: 'same-origin', cache: 'no-store' })
    .then((res) => {
      const server = Date.parse(res.headers.get('date') ?? '');
      if (!Number.isFinite(server)) return;
      const estimate = server + 500 - (t0 + Date.now()) / 2; // the header is truncated to the second → +0.5 s
      if (Math.abs(estimate) < 1500) return; // sub-second noise: keep the local clock
      offsetMs = estimate;
      for (const t of tickers.values()) {
        t.value = floorTo(t.interval);
        t.listeners.forEach((l) => l());
      }
    })
    .catch(() => undefined);
}

function tickerFor(interval: number): Ticker {
  let t = tickers.get(interval);
  if (!t) tickers.set(interval, (t = { interval, value: floorTo(interval), listeners: new Set(), timer: null }));
  return t;
}

function schedule(t: Ticker) {
  const delay = t.interval - (serverNow() % t.interval) + 5; // align to boundaries: clock seconds flip together
  t.timer = setTimeout(() => {
    t.value = floorTo(t.interval);
    t.listeners.forEach((l) => l());
    if (t.listeners.size) schedule(t);
    else t.timer = null;
  }, delay);
}

const subscribers = new Map<number, (listener: () => void) => () => void>();
function subscriberFor(interval: number) {
  let s = subscribers.get(interval);
  if (!s) {
    s = (listener) => {
      syncServerOffset();
      const t = tickerFor(interval);
      t.listeners.add(listener);
      if (!t.timer) {
        t.value = floorTo(interval);
        schedule(t);
      }
      return () => {
        t.listeners.delete(listener);
        if (!t.listeners.size && t.timer) {
          clearTimeout(t.timer);
          t.timer = null;
        }
      };
    };
    subscribers.set(interval, s);
  }
  return s;
}

/**
 * Server time in ms, floored to `intervalMs` and re-rendering on aligned boundaries
 * (1000 → every second, all clocks flip together; 60_000 → every minute).
 */
export function useServerNow(intervalMs = 1000): number {
  return useSyncExternalStore(
    subscriberFor(intervalMs),
    () => tickerFor(intervalMs).value,
    () => Math.floor(Date.now() / intervalMs) * intervalMs,
  );
}

/** Alias for staleness checks. */
export const useNow = useServerNow;
